import { pathOr } from 'ramda';
import { createApi } from '@reduxjs/toolkit/query/react';
import { gql } from 'graphql-request';
import { graphqlRequestBaseQuery } from '@rtk-query/graphql-request-base-query';
import { windowExists } from '@classity-edtech/ui-base';
import { SEO_CONTENT } from '../store/actionTypes';
import { navQuery, pageQuery, sectionQuery, variable } from '../utils/contentTypeQueries';
import { ContentResponse, ContentVariable, PageContent, SectionContent } from './models/seo';

export const seoContent = createApi({
  reducerPath: SEO_CONTENT,

  baseQuery: graphqlRequestBaseQuery({
    url: `${pathOr('', ['graphqlService', 'url'], windowExists?.__UI_CONFIG__)}`,
    prepareHeaders: headers => {
      headers.set(
        'Authorization',
        `Bearer ${(windowExists?.localStorage as any).getItem('token')}`,
      );
      headers.set('x-ctt-source-project', `${windowExists?.__UI_CONFIG__.project}`);

      return headers;
    },
  }),

  tagTypes: ['SeoContent'],

  endpoints: builder => ({
    getPageContent: builder.query<PageContent, ContentVariable>({
      query: ({ contentType, tag }) => ({
        document: gql`
          ${pageQuery}
        `,
        variables: variable({ contentType, tag }),
      }),

      transformResponse: (response: any) => {
        const data = response?.entries?.items?.[0]?.fields;

        return {
          seo: data?.seo,
          header: data?.header,
          footer: data?.footer,
          sections: data?.sections,
        };
      },
    }),

    getSectionContent: builder.query<SectionContent, ContentVariable>({
      query: ({ contentType, tag }) => ({
        document: gql`
          ${sectionQuery}
        `,
        variables: variable({ contentType, tag }),
      }),

      transformResponse: (response: ContentResponse) => {
        const data = response?.entries?.items?.[0]?.fields;

        return {
          seo: data?.seo,
          header: data?.header,
          footer: data?.footer,
          sections: data?.sections,
        };
      },
    }),

    getNavContent: builder.query<
      Pick<PageContent, 'footer' | 'header' | 'contentType'>,
      ContentVariable
    >({
      query: ({ contentType }) => ({
        document: gql`
          ${navQuery}
        `,
        variables: variable({ contentType }),
      }),

      transformResponse: (response: ContentResponse) => {
        const data = response?.entries?.items?.[0]?.fields;

        return {
          header: data?.header,
          footer: data?.footer,
        };
      },
    }),

    getAuthContent: builder.query<Pick<PageContent, 'sections'>, ContentVariable>({
      query: ({ contentType }) => ({
        document: gql`
          query Page($query: JSON!) {
            entries(query: $query) {
              limit
              skip
              sys
              total
              items {
                ...PageFragment
              }
            }
          }

          fragment PageFragment on Entry {
            fields {
              __typename
              ... on EntryPage {
                sections {
                  fields {
                    description {
                      content {
                        content {
                          value
                        }
                      }
                    }
                    title
                    list {
                      fields {
                        title
                        description {
                          content {
                            content {
                              value
                            }
                          }
                        }
                        media {
                          fields {
                            ... on ImageWrapper {
                              image {
                                fields {
                                  file {
                                    url
                                  }
                                }
                              }
                            }
                          }
                        }
                        data
                      }
                    }
                  }
                }
              }
            }
          }
        `,
        variables: {
          query: {
            content_type: contentType,
            include: 8,
          },
        },
      }),

      transformResponse: (response: any) => {
        const data = response?.entries?.items?.[0]?.fields;

        return {
          sections: data?.sections,
        };
      },
    }),
  }),
});

export const { useGetNavContentQuery, useGetPageContentQuery, useGetSectionContentQuery } =
  seoContent;
